import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, Title);

const Wrapper = styled.div`
  width: 100%;
  height: 50vh;
  padding: 24px;
  position: relative;
`;

const InfoIconWrapper = styled.div`
  position: absolute;
  top: 12px;
  right: 16px;
  cursor: pointer;
  color: #666;
  &:hover { color: #000; }
  svg { width: 20px; height: 20px; }
`;

// Purchase color scheme
const PURPLE = "#9848c7";                          // Krajowe
const PURPLE_BORDER = "rgba(152,72,199,1)";
const LIGHT_PURPLE = "rgba(152,72,199,0.35)";      // UE
const LIGHT_PURPLE_BORDER = "rgba(152,72,199,0.8)";
const RED = "rgba(220,53,69,0.7)";                 // Import (poza UE)
const RED_BORDER = "rgba(220,53,69,1)";

const StrukturaZakupowChart = ({ data }) => {
  const [chartData, setChartData] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);

  useEffect(() => {
    if (!data || data.length === 0) return;

    const years = [
      ...new Set(
        data
          .map((row) => row.DataZakupu)
          .filter(Boolean)
          .map((d) => new Date(d).getFullYear())
      ),
    ];
    const latest = Math.max(...years);
    setAvailableYears(years.sort((a, b) => a - b));
    setSelectedYear(latest);
  }, [data]);

  useEffect(() => {
    if (!data || data.length === 0 || !selectedYear) return;

    const months = Array.from({ length: 12 }, (_, i) => ({
      month: i + 1,
      import: 0,
      eu: 0,
      domestic: 0,
    }));

    data.forEach((row) => {
      const date = new Date(row.DataZakupu);
      if (isNaN(date) || date.getFullYear() !== selectedYear) return;

      const month = date.getMonth();
      const value = parseFloat(row.K_42) || 0;
      if (value <= 0) return;

      if (row.KodKrajuNadaniaTIN && row.KodKrajuNadaniaTIN !== "PL") {
        const euCodes = ["AT","BE","BG","HR","CY","CZ","DK","EE","FI","FR","DE","GR","HU","IE","IT","LV","LT","LU","MT","NL","PL","PT","RO","SK","SI","ES","SE"]; // EU list
        if (euCodes.includes(row.KodKrajuNadaniaTIN)) {
          months[month].eu += value;
        } else {
          months[month].import += value;
        }
      } else {
        months[month].domestic += value;
      }
    });

    const labels = [
      "Styczeń","Luty","Marzec","Kwiecień","Maj","Czerwiec",
      "Lipiec","Sierpień","Wrzesień","Październik","Listopad","Grudzień",
    ];

    setChartData({
      labels,
      datasets: [
        {
          label: "Import (poza UE)",
          data: months.map((m) => m.import),
          backgroundColor: RED,
          borderColor: RED_BORDER,
          borderWidth: 1,
        },
        {
          label: "UE",
          data: months.map((m) => m.eu),
          backgroundColor: LIGHT_PURPLE,
          borderColor: LIGHT_PURPLE_BORDER,
          borderWidth: 1,
        },
        {
          label: "Krajowe",
          data: months.map((m) => m.domestic),
          backgroundColor: PURPLE,
          borderColor: PURPLE_BORDER,
          borderWidth: 1,
        },
      ],
    });
  }, [data, selectedYear]);

  if (!chartData) return <p>Ładowanie wykresu...</p>;

  return (
    <Wrapper>
      <InfoIconWrapper data-tooltip-id="structure-purchases-tooltip">
        <Info />
      </InfoIconWrapper>

      <InfoTooltip
        id="structure-purchases-tooltip"
        place="left"
        content={
          <>
            <strong>Miesięczna struktura zakupów</strong>
            <br />
            Podział: Import (czerwony), UE (jasny fiolet), Krajowe (fiolet).
          </>
        }
      />

      <div style={{ marginBottom: 16 }}>
        <label>
          Rok:&nbsp;
          <select
            value={selectedYear ?? ""}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            style={{ padding: "6px 10px", fontSize: 14, borderRadius: 5, cursor: "pointer" }}
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
      </div>

      <div style={{ height: 400 }}>
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { position: "top" },
              title: {
                display: true,
                text: `Miesięczna struktura zakupów - ${selectedYear}`,
              },
              tooltip: {
                callbacks: {
                  label: (ctx) =>
                    `${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString("pl-PL", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`,
                },
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                title: { display: true, text: "Wartość zakupów" },
                ticks: {
                  callback: (value) =>
                    value.toLocaleString("pl-PL", {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    }),
                },
              },
            },
          }}
        />
      </div>
    </Wrapper>
  );
};

export default StrukturaZakupowChart;
